// WebReader.js
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { AuthContext } from '../context/AuthContext';
import './WebReader.css';

function WebReader() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const contentRef = useRef(null);
  
  const [bookData, setBookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fontSize, setFontSize] = useState(100); // percentage
  const [showSettings, setShowSettings] = useState(false);
  const [theme, setTheme] = useState('light'); // light, sepia, dark

  const fetchBookContent = async () => {
    try {
      const response = await api.get(`/api/read/${token}/`);
      setBookData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching book content:', err);
      if (err.response?.status === 404) {
        setError('Book not found or access denied.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to read this book.');
      } else {
        setError('Failed to load book content. Please try again.');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      // Store the intended destination and redirect to login
      sessionStorage.setItem('redirectAfterLogin', `/read/${token}`);
      navigate('/login');
      return;
    }

    if (user && token) {
      fetchBookContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, token, navigate]);

  const adjustFontSize = (delta) => {
    setFontSize(prev => Math.min(Math.max(prev + delta, 75), 150));
  };

  const scrollToTop = () => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (authLoading || loading) {
    return (
      <div className="reader-loading">
        <div className="loading-spinner"></div>
        <p>Loading your book...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reader-error">
        <div className="error-icon">📚</div>
        <h2>Unable to Load Book</h2>
        <p>{error}</p>
        <Link to="/my-downloads" className="back-link">← Back to My Downloads</Link>
      </div>
    );
  }

  return (
    <div className={`web-reader theme-${theme}`}>
      {/* Header Bar */}
      <header className="reader-header">
        <div className="header-left">
          <Link to="/my-downloads" className="back-btn" title="Back to Downloads">
            ← Back
          </Link>
          <h1 className="book-title">{bookData?.title}</h1>
        </div>
        
        <div className="header-right">
          <button 
            className="settings-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Reading Settings"
          >
            ⚙️ Settings
          </button>
        </div>
      </header>

      {/* Settings Panel */}
      {showSettings && (
        <div className="settings-panel">
          <div className="setting-group">
            <label>Font Size</label>
            <div className="font-controls">
              <button onClick={() => adjustFontSize(-10)} disabled={fontSize <= 75}>A-</button>
              <span>{fontSize}%</span>
              <button onClick={() => adjustFontSize(10)} disabled={fontSize >= 150}>A+</button>
            </div>
          </div>
          
          <div className="setting-group">
            <label>Theme</label>
            <div className="theme-controls">
              <button 
                className={`theme-btn light ${theme === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
                title="Light"
              >☀️</button>
              <button 
                className={`theme-btn sepia ${theme === 'sepia' ? 'active' : ''}`}
                onClick={() => setTheme('sepia')}
                title="Sepia"
              >📜</button>
              <button 
                className={`theme-btn dark ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
                title="Dark"
              >🌙</button>
            </div>
          </div>
        </div>
      )}

      {/* License Banner */}
      <div className="license-banner">
        <span>📖 Licensed to: <strong>{bookData?.licensed_to}</strong></span>
        <span className="license-notice">Personal reading only • Do not share</span>
      </div>

      {/* Book Content */}
      <main 
        className="reader-content" 
        ref={contentRef}
        style={{ fontSize: `${fontSize}%` }}
      >
        {/* Cover Image */}
        {bookData?.cover_url && (
          <div className="book-cover">
            <img src={bookData.cover_url} alt={`${bookData.title} cover`} />
          </div>
        )}

        {/* Title Page */}
        <div className="title-page">
          <h1>{bookData?.title}</h1>
          <p className="publisher">Christensen Kirtis Publishing</p>
        </div>

        {/* Main Content */}
        <article 
          className="book-body"
          dangerouslySetInnerHTML={{ __html: bookData?.content }}
        />

        {/* Footer License */}
        <footer className="content-footer">
          <p>© Christensen Kirtis Publishing. All rights reserved.</p>
          <p>This e-book is licensed to <strong>{bookData?.licensed_to}</strong> for personal reading only.</p>
        </footer>
      </main>

      {/* Scroll to Top Button */}
      <button className="scroll-top-btn" onClick={scrollToTop} title="Scroll to top">
        ↑
      </button>
    </div>
  );
}

export default WebReader;
